/**
 * PROFILS (v0.5.0, B-602, ART-0175 § 1) — un profil est un MÉTIER EXERCÉ ; il choisit la VUE
 * d'accueil. Le RÔLE D'ACCÈS (propriétaire, contributeur, lecteur) choisit les DROITS et reste
 * lu ailleurs (`useEffectiveNavRoles`) : rien ici n'en dérive un droit.
 *
 * Une personne peut cumuler des profils et basculer de vue en un geste ; la vue « combinée »
 * rassemble les blocs des profils cumulés (les cinq de la v0.5.0 si aucun n'est déclaré).
 *
 * Lot RP (revue par métier du 2026-10-01, manque 24, SP-15) : Scrum Master, architecte et sponsor
 * ont désormais leur accueil (V541 côté passerelle).
 */
import type { ProfileId, ProfileView } from '@/api/types/profile'

/** Ordre canonique : celui de la planche `Nav` et de la passerelle (`ProfilePreferenceService`). */
export const PROFILS: readonly ProfileId[] = ['metier', 'po', 'dev', 'testeur', 'release', 'scrum_master', 'architecte', 'sponsor']

/** Profils montrés par la vue combinée quand aucun n'est déclaré : les cinq de la v0.5.0. */
export const PROFILS_COMBINES_PAR_DEFAUT: readonly ProfileId[] = ['metier', 'po', 'dev', 'testeur', 'release']

export const VUES: readonly ProfileView[] = [...PROFILS, 'combinee', 'administration']

/**
 * Lot NAV 0.5.x (registre n° 12) : la vue « administration » n'est PROPOSÉE qu'à qui peut
 * administrer (super-administrateur, administrateur d'organisation) ; ailleurs elle n'aurait rien
 * à montrer. Ce filtre ne protège rien : chaque bloc lit une route gardée par la passerelle.
 */
export function vuesProposees(peutAdministrer: boolean): readonly ProfileView[] {
  return peutAdministrer ? VUES : VUES.filter((v) => v !== 'administration')
}

/** Libellés français (replis de `t('profil.<id>')`). */
export const LIBELLES_VUE: Record<ProfileView, string> = {
  metier: 'Métier',
  po: 'Product owner',
  dev: 'Développeur',
  testeur: 'Testeur',
  release: 'Release manager',
  scrum_master: 'Scrum Master',
  architecte: 'Architecte',
  sponsor: 'Sponsor',
  combinee: 'Vue combinée',
  administration: 'Administration',
}

/**
 * Ce que chaque vue montre en premier (ART-0175 § 1, ligne « Voit en premier ») — titre de l'accueil.
 * Clés littérales (lot RP) : chaque texte est extrait et vérifié dans les deux catalogues, au lieu
 * d'un repli en dur passé à une clé construite.
 */
export function questionVue(vue: ProfileView, t: (cle: string, repli: string) => string): string {
  switch (vue) {
    case 'metier': return t('accueil.titre.metier', 'Les demandes du projet et ce qui attend ma décision')
    case 'po': return t('accueil.titre.po', 'Le sprint en cours, les missions de la colonie et ce qui est vraiment fini')
    case 'dev': return t('accueil.titre.dev', 'Ce que la colonie a produit à revoir, et les tâches où elle s’est arrêtée')
    case 'testeur': return t('accueil.titre.testeur', 'La campagne en cours, les anomalies à vérifier et les exigences sans test')
    case 'release': return t('accueil.titre.release', 'La version est-elle prête ? Ce qui a été livré, à qui, avec quelles preuves')
    case 'scrum_master': return t('accueil.titre.scrum_master', 'Le sprint actif, ses obstacles et le travail en cours')
    case 'architecte': return t('accueil.titre.architecte', 'Les décisions et les dérogations qui attendent votre avis')
    case 'sponsor': return t('accueil.titre.sponsor', 'Ce qui attend votre approbation, et où en est le programme')
    case 'administration': return t('accueil.titre.administration', 'Ce qui attend une décision d’administration, et les raccourcis')
    default: return t('accueil.titre.combinee', 'Les blocs de chacun de vos profils, sur une seule page')
  }
}

export function estVue(valeur: unknown): valeur is ProfileView {
  return typeof valeur === 'string' && (VUES as readonly string[]).includes(valeur)
}

export type RoleAcces = 'owner' | 'contributor' | 'viewer' | 'admin'

/**
 * Le rôle d'accès effectif de l'espace, pour l'INDICATEUR (jamais pour choisir la vue à la place
 * de la personne). Le rôle DE L'ESPACE prime ; l'administrateur de la plateforme sans appartenance
 * est dit tel quel, jamais « propriétaire » d'un espace qu'il ne possède pas.
 */
export function roleAcces(roles: string[]): RoleAcces {
  if (roles.includes('workspace_owner')) return 'owner'
  if (roles.includes('workspace_contributor')) return 'contributor'
  if (roles.includes('workspace_viewer')) return 'viewer'
  if (roles.includes('admin')) return 'admin'
  return 'viewer'
}

export const LIBELLES_ROLE: Record<RoleAcces, string> = {
  owner: 'propriétaire',
  contributor: 'contributeur',
  viewer: 'lecteur',
  admin: 'administrateur de la plateforme',
}

/**
 * Vue SUGGÉRÉE tant que la personne n'a rien choisi dans l'espace. Ce n'est qu'une
 * proposition, affichée comme telle : le propriétaire pilote souvent le produit (PO), le
 * contributeur construit (développeur), le lecteur suit la demande (métier). Le premier choix
 * explicite la remplace, et le rôle ne change jamais ce que la vue permet.
 */
export function vueSuggeree(role: RoleAcces): ProfileView {
  // Lot NAV 0.5.x : l'administrateur de la plateforme SANS rôle dans l'espace se voit proposer
  // l'accueil d'administration — « Product owner » ne décrivait pas son travail.
  if (role === 'admin') return 'administration'
  return role === 'owner' ? 'po' : role === 'contributor' ? 'dev' : 'metier'
}

/**
 * B-604 — REGISTRE DE VOCABULAIRE par défaut (ART-0175 § 7) : métier pour les profils métier et
 * PO, technique pour développeur, testeur et release manager. La vue combinée garde le registre
 * métier : elle s'adresse d'abord à une personne qui porte plusieurs casquettes, pas à un
 * spécialiste. Le choix explicite de la bascule « Détails techniques » l'emporte toujours.
 */
export function registreParDefaut(vue: ProfileView): 'metier' | 'technique' {
  return vue === 'dev' || vue === 'testeur' || vue === 'release' || vue === 'architecte' ? 'technique' : 'metier'
}

/** Profils dont la vue combinée affiche les blocs : ceux déclarés, sinon les cinq de la v0.5.0. */
export function profilsCombines(profils: readonly ProfileId[]): ProfileId[] {
  return profils.length > 0 ? PROFILS.filter((p) => profils.includes(p)) : [...PROFILS_COMBINES_PAR_DEFAUT]
}
